import React from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Modal, Form, Input, InputNumber, Select, DatePicker, Space, Typography, Tag, Popconfirm, Drawer, Descriptions, notification, Alert } from 'antd';
import { PlusOutlined, EditOutlined, StopOutlined, ThunderboltOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useBooks, BooksSelector, BooksCaption } from '../../lib/books';

import { date as fmtDate } from '../../lib/format';
const money = (v) => Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Fixed Asset Register (xlsx "FAR") of one set of books: the assets, depreciation runs per period, disposals. The report itself is under Ledger Reports. */
export default function FixedAssetsPage() {
  const auth = useAuth();
  const isManager = auth.user.role === 'SYSTEM_ADMIN' || auth.user.role === 'COMPANY_MANAGER';
  const { ledgerId } = useBooks();
  const [rows, setRows] = React.useState([]);
  const [accounts, setAccounts] = React.useState([]);
  const [refs, setRefs] = React.useState({ departments: [], branches: [], projects: [] });
  const [loading, setLoading] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [disposing, setDisposing] = React.useState(null);
  const [detail, setDetail] = React.useState(null);
  const [runOpen, setRunOpen] = React.useState(false);
  const [runPreview, setRunPreview] = React.useState(null);
  const [runRange, setRunRange] = React.useState([moment().startOf('year'), moment().endOf('year')]);
  const [busy, setBusy] = React.useState(false);
  const [form] = Form.useForm();
  const [disposeForm] = Form.useForm();

  React.useEffect(() => {
    if (!ledgerId) return;
    const q = { ledger_id: ledgerId };
    request.get('/accounts', q).then((r) => r.json()).then((r) => r.status && setAccounts(r.data.filter((a) => !a.is_heading)));
    Promise.all([request.get('/departments', q).then((r) => r.json()), request.get('/branches', q).then((r) => r.json()), request.get('/projects', q).then((r) => r.json())]).then(([d, b, p]) =>
      setRefs({ departments: d.status ? d.data : [], branches: b.status ? b.data : [], projects: p.status ? p.data : [] })
    );
  }, [ledgerId]);

  const fetchRows = () => {
    if (!ledgerId) return setRows([]);
    setLoading(true);
    request.get('/fixed-assets', { ledger_id: ledgerId }).then((r) => r.json()).then((r) => r.status && setRows(r.data)).finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchRows, [ledgerId]);

  const farAccounts = accounts.filter((a) => a.shows_on_far || a.has_depreciation);
  const costOptions = (farAccounts.length ? farAccounts : accounts).map((a) => ({ value: a.id, label: `${a.code} ${a.name}` }));
  const allOptions = accounts.map((a) => ({ value: a.id, label: `${a.code} ${a.name}` }));

  const openNew = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ acquisition_date: moment(), residual_value: 0, depreciation_rate: 20 });
    setOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    form.setFieldsValue({ ...a, acquisition_date: moment(a.acquisition_date), cost: Number(a.cost), residual_value: Number(a.residual_value), depreciation_rate: Number(a.depreciation_rate) });
    setOpen(true);
  };
  const save = () => {
    form.validateFields().then((values) => {
      const payload = { ...values, acquisition_date: values.acquisition_date.format('YYYY-MM-DD') };
      (editing ? request.put(`/fixed-assets/${editing.id}`, payload) : request.post('/fixed-assets', { ...payload, ledger_id: ledgerId }))
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: `${r.data.asset_code} saved` });
            setOpen(false);
            fetchRows();
          } else notification.error({ message: r.message });
        });
    });
  };
  const dispose = () => {
    disposeForm.validateFields().then((values) => {
      request
        .post(`/fixed-assets/${disposing.id}/dispose`, { disposal_date: values.disposal_date.format('YYYY-MM-DD'), disposal_proceeds: values.disposal_proceeds || 0 })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: r.message });
            setDisposing(null);
            fetchRows();
          } else notification.error({ message: r.message });
        });
    });
  };
  const remove = (a) => request.delete(`/fixed-assets/${a.id}`).then((r) => r.json()).then((r) => (r.status ? fetchRows() : notification.error({ message: r.message })));
  const openDetail = (a) => request.get(`/fixed-assets/${a.id}`).then((r) => r.json()).then((r) => r.status && setDetail(r.data));

  const loadPreview = (range) => {
    request
      .get('/fixed-assets/depreciation/preview', { ledger_id: ledgerId, from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') })
      .then((r) => r.json())
      .then((r) => (r.status ? setRunPreview(r.data) : notification.error({ message: r.message })));
  };
  const runDepreciation = () => {
    setBusy(true);
    request
      .post('/fixed-assets/depreciation/run', { ledger_id: ledgerId, from: runRange[0].format('YYYY-MM-DD'), to: runRange[1].format('YYYY-MM-DD') })
      .then((r) => r.json())
      .then((r) => {
        if (r.status) {
          notification.success({ message: r.message });
          setRunOpen(false);
          fetchRows();
        } else notification.error({ message: r.message });
      })
      .finally(() => setBusy(false));
  };

  const columns = [
    { title: 'Code', dataIndex: 'asset_code', width: 120, render: (v, a) => <Typography.Link onClick={() => openDetail(a)}>{v}</Typography.Link> },
    { title: 'Description', dataIndex: 'description' },
    { title: 'Asset account', key: 'acc', render: (_, a) => (a.Account ? `${a.Account.code} ${a.Account.name}` : '-') },
    { title: 'Acquired', dataIndex: 'acquisition_date', width: 110, render: (v) => fmtDate(v) },
    { title: 'Cost', dataIndex: 'cost', align: 'right', width: 110, render: money },
    { title: 'Rate', dataIndex: 'depreciation_rate', width: 70, render: (v) => `${Number(v)}%` },
    { title: 'Status', dataIndex: 'status', width: 100, render: (v, a) => (v === 'DISPOSED' ? <Tag color="red">DISPOSED {a.disposal_date}</Tag> : <Tag color="green">ACTIVE</Tag>) },
    {
      title: '',
      key: 'actions',
      width: 130,
      render: (_, a) => (
        <Space size={4}>
          <Button size="small" icon={<EyeOutlined />} onClick={() => openDetail(a)} />
          {a.status === 'ACTIVE' && <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(a)} />}
          {a.status === 'ACTIVE' && isManager && (
            <Button size="small" icon={<StopOutlined />} title="Dispose" onClick={() => { setDisposing(a); disposeForm.resetFields(); disposeForm.setFieldsValue({ disposal_date: moment(), disposal_proceeds: 0 }); }} />
          )}
          {a.status === 'ACTIVE' && isManager && (
            <Popconfirm title="Remove this asset? Only possible before any depreciation is posted." onConfirm={() => remove(a)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 16 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Fixed Assets
          </Typography.Title>
          <div><BooksCaption /></div>
          <Typography.Text type="secondary">
            The asset register behind the FAR report (<Link to="/ledger-reports?tab=far">Ledger Reports → Fixed Asset Register</Link>). Straight-line depreciation is run per period and posted to the ledger.
          </Typography.Text>
        </div>
        <Space wrap>
          <BooksSelector />
          {isManager && (
            <Button icon={<ThunderboltOutlined />} disabled={!ledgerId} onClick={() => { setRunOpen(true); loadPreview(runRange); }}>
              Run depreciation
            </Button>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={openNew} disabled={!ledgerId}>
            New Asset
          </Button>
        </Space>
      </div>

      <Table rowKey="id" size="small" columns={columns} dataSource={rows} loading={loading} pagination={{ pageSize: 25 }} />

      <Modal title={editing ? `Edit ${editing.asset_code}` : 'New Asset'} open={open} onCancel={() => setOpen(false)} onOk={save} width={760}>
        <Form form={form} layout="vertical">
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input maxLength={200} placeholder="e.g. Toyota Corolla ABC 123" />
          </Form.Item>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="account_id" label="Asset (cost) account" rules={[{ required: true }]} style={{ width: 350 }} tooltip="The accumulated depreciation account defaults to this account's '- Depreciation' sub-account">
              <Select showSearch optionFilterProp="label" options={costOptions} />
            </Form.Item>
            <Form.Item name="depreciation_account_id" label="Accumulated depreciation account" style={{ width: 350 }}>
              <Select allowClear showSearch optionFilterProp="label" options={allOptions} placeholder="Auto from the cost account" />
            </Form.Item>
          </Space>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="acquisition_date" label="Acquisition date" rules={[{ required: true }]} style={{ width: 160 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="cost" label="Cost (EUR)" rules={[{ required: true }]} style={{ width: 160 }}>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="residual_value" label="Residual value" style={{ width: 150 }}>
              <InputNumber min={0} precision={2} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="depreciation_rate" label="Depreciation % p.a." rules={[{ required: true }]} style={{ width: 150 }}>
              <InputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="expense_account_id" label="Depreciation expense account" style={{ width: 350 }} tooltip="Leave empty to use the books' default">
              <Select allowClear showSearch optionFilterProp="label" options={allOptions} />
            </Form.Item>
            <Form.Item name="supplier_name" label="Supplier" style={{ width: 200 }}>
              <Input maxLength={150} />
            </Form.Item>
            <Form.Item name="invoice_reference" label="Invoice ref." style={{ width: 150 }}>
              <Input maxLength={120} />
            </Form.Item>
          </Space>
          <Space style={{ display: 'flex' }} align="start">
            <Form.Item name="department_id" label="Department" style={{ width: 230 }}>
              <Select allowClear options={refs.departments.map((d) => ({ value: d.id, label: d.name }))} />
            </Form.Item>
            <Form.Item name="branch_id" label="Branch" style={{ width: 230 }}>
              <Select allowClear options={refs.branches.map((b) => ({ value: b.id, label: `${b.code} ${b.name}` }))} />
            </Form.Item>
            <Form.Item name="project_id" label="Project" style={{ width: 230 }}>
              <Select allowClear options={refs.projects.map((p) => ({ value: p.id, label: p.name }))} />
            </Form.Item>
          </Space>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={disposing ? `Dispose ${disposing.asset_code} - ${disposing.description}` : ''} open={!!disposing} onCancel={() => setDisposing(null)} onOk={dispose} okText="Dispose">
        <Typography.Paragraph type="secondary">Run depreciation up to the disposal date first. The disposal posts accumulated depreciation and proceeds against the cost, with the gain or loss to the books' disposal account.</Typography.Paragraph>
        <Form form={disposeForm} layout="vertical">
          <Form.Item name="disposal_date" label="Disposal date" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="disposal_proceeds" label="Proceeds (EUR)">
            <InputNumber min={0} precision={2} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Run depreciation" open={runOpen} onCancel={() => setRunOpen(false)} onOk={runDepreciation} okText="Post depreciation" confirmLoading={busy} width={760}>
        <Space style={{ marginBottom: 12 }}>
          <span>Period</span>
          <DatePicker.RangePicker value={runRange} allowClear={false} onChange={(v) => { if (v) { setRunRange(v); loadPreview(v); } }} />
        </Space>
        <Table
          size="small"
          rowKey="asset_id"
          pagination={false}
          dataSource={runPreview || []}
          locale={{ emptyText: 'No assets' }}
          columns={[
            { title: 'Asset', key: 'a', render: (_, p) => `${p.asset_code} ${p.description}` },
            { title: 'Account', dataIndex: 'account' },
            { title: 'Cost', dataIndex: 'cost', align: 'right', render: money },
            { title: 'Rate', dataIndex: 'rate', render: (v) => `${v}%` },
            { title: 'Accumulated', dataIndex: 'accumulated', align: 'right', render: money },
            { title: 'Charge', dataIndex: 'charge', align: 'right', render: (v, p) => (p.already_run ? <Tag>already run</Tag> : <strong>{money(v)}</strong>) },
          ]}
        />
        {runPreview && runPreview.some((p) => !p.has_accounts && p.charge > 0) && <Alert type="warning" showIcon style={{ marginTop: 8 }} message="Some assets have no accumulated depreciation account; set one before running." />}
      </Modal>

      <Drawer title={detail ? `${detail.asset_code} - ${detail.description}` : ''} open={!!detail} onClose={() => setDetail(null)} width={760}>
        {detail && (
          <div>
            <Descriptions size="small" bordered column={2} style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Asset account">{detail.Account ? `${detail.Account.code} ${detail.Account.name}` : '-'}</Descriptions.Item>
              <Descriptions.Item label="Accumulated dep. a/c">{detail.DepreciationAccount ? `${detail.DepreciationAccount.code} ${detail.DepreciationAccount.name}` : '-'}</Descriptions.Item>
              <Descriptions.Item label="Acquired">{detail.acquisition_date}</Descriptions.Item>
              <Descriptions.Item label="Cost / residual">{money(detail.cost)} / {money(detail.residual_value)}</Descriptions.Item>
              <Descriptions.Item label="Rate">{Number(detail.depreciation_rate)}% straight-line</Descriptions.Item>
              <Descriptions.Item label="Status">{detail.status}{detail.disposal_date ? ` on ${detail.disposal_date} for ${money(detail.disposal_proceeds)}` : ''}{detail.DisposalEntry ? ` (${detail.DisposalEntry.entry_number})` : ''}</Descriptions.Item>
              <Descriptions.Item label="Supplier / ref">{[detail.supplier_name, detail.invoice_reference].filter(Boolean).join(' - ') || '-'}</Descriptions.Item>
              <Descriptions.Item label="Analysis">{[detail.Department?.name, detail.Branch?.name, detail.Project?.name].filter(Boolean).join(' / ') || '-'}</Descriptions.Item>
            </Descriptions>
            <Typography.Text strong>Depreciation history</Typography.Text>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={detail.AssetDepreciationEntries || []}
              locale={{ emptyText: 'No depreciation posted yet' }}
              columns={[
                { title: 'Period', key: 'p', render: (_, e) => `${e.period_start} to ${e.period_end}` },
                { title: 'Charge', dataIndex: 'amount', align: 'right', render: money },
                { title: 'Journal', key: 'j', render: (_, e) => (e.JournalEntry ? `${e.JournalEntry.entry_number} (${e.JournalEntry.status})` : '-') },
              ]}
            />
          </div>
        )}
      </Drawer>
    </div>
  );
}
